import { useEffect, useState } from "react";
import api from "@/lib/api";

const fmtAnnoScol = (a) => {
  const n = Number(a);
  if (!n) return "—";
  return `${n}/${String(n + 1).slice(-2)}`;
};

// Converte ISO UTC → stringa "YYYY-MM-DDTHH:mm" in Europe/Rome (per <input type="datetime-local">)
function isoUtcToRomeLocalInput(isoUtc) {
  if (!isoUtc) return "";
  try {
    const d = new Date(isoUtc);
    // Usa Intl per ottenere components in Europe/Rome
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Rome",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(d);
    const o = Object.fromEntries(parts.map(p => [p.type, p.value]));
    return `${o.year}-${o.month}-${o.day}T${o.hour}:${o.minute}`;
  } catch { return ""; }
}

// Converte "YYYY-MM-DDTHH:mm" (interpretata come Europe/Rome) → ISO UTC
function romeLocalInputToIsoUtc(localStr) {
  if (!localStr) return null;
  try {
    // Calcola l'offset di Europe/Rome per quella data (CET/CEST)
    // Truc: costruisci due date e prendi la differenza
    const asLocal = new Date(localStr); // browser-local
    // Costruisci una data che, formattata in Europe/Rome, dà gli stessi componenti
    // Approccio robusto: prova con offset standard, calcola il vero offset usando Intl
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Rome",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }).formatToParts(asLocal);
    const o = Object.fromEntries(parts.map(p => [p.type, p.value]));
    // asLocal formattata in Europe/Rome dà o.hour:o.minute; se diverso dall'input,
    // la differenza è l'offset attuale del browser rispetto a Rome.
    const [d, t] = localStr.split("T");
    const [y, m, dd] = d.split("-").map(Number);
    const [hh, mm] = t.split(":").map(Number);
    // Calcola UTC come se il tempo locale fosse Europe/Rome
    // Strategia: crea un Date in UTC con quei componenti, poi correggi con offset di Rome
    const utcGuess = Date.UTC(y, m - 1, dd, hh, mm, 0);
    // Verifica: formatta utcGuess in Europe/Rome
    const verifyParts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Rome",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(utcGuess));
    const v = Object.fromEntries(verifyParts.map(p => [p.type, p.value]));
    const verifiedH = Number(v.hour);
    const verifiedDay = Number(v.day);
    // diff in minuti tra l'ora target (hh) e quella ottenuta in Rome
    let diffMin = (hh - verifiedH) * 60;
    // Se cambia il giorno (es. mezzanotte), aggiusta di ±24h
    if (verifiedDay !== dd) {
      const dayDiff = dd - verifiedDay;
      // dayDiff potrebbe essere ±1 o anche ±30/-30 a cavallo di mese: limita
      if (Math.abs(dayDiff) === 1) diffMin += dayDiff * 24 * 60;
    }
    const utcMs = utcGuess + diffMin * 60_000;
    return new Date(utcMs).toISOString();
  } catch { return null; }
}

function fmtRomaUmana(isoUtc) {
  if (!isoUtc) return "—";
  try {
    const d = new Date(isoUtc);
    return d.toLocaleString("it-IT", {
      timeZone: "Europe/Rome",
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) + " (ora italiana)";
  } catch { return "—"; }
}

export default function ExtraImpostazioniView() {
  const [cfg, setCfg] = useState(null);
  const [anni, setAnni] = useState([]);
  const [annoIscr, setAnnoIscr] = useState("");
  const [stato, setStato] = useState("aperto");
  const [openLocal, setOpenLocal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/mensa-matrice/iscrizioni-config"),
        api.get("/mensa-matrice/anni"),
      ]);
      setCfg(c.data);
      const ai = c.data?.anno_iscrizioni;
      setAnnoIscr(ai == null ? "" : String(ai));
      setStato(c.data?.stato || "aperto");
      setOpenLocal(isoUtcToRomeLocalInput(c.data?.open_at));
      setAnni(a.data?.anni || []);
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore caricamento configurazione" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (stato === "programmato" && !openLocal) {
      setMsg({ ok: false, text: "Imposta una data/ora di apertura quando lo stato è 'Programmato'" });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      const payload = {
        anno_iscrizioni: annoIscr === "" ? null : Number(annoIscr),
        stato,
        open_at: stato === "programmato" ? romeLocalInputToIsoUtc(openLocal) : null,
      };
      const r = await api.put("/mensa-matrice/iscrizioni-config", payload);
      setMsg({ ok: true, text: "Configurazione salvata" });
      await load();
      return r.data;
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore salvataggio" });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Azzerare configurazione?\n- Anno iscrizioni: AUTOMATICO (anno alunno)\n- Stato portale: APERTO")) return;
    setAnnoIscr("");
    setStato("aperto");
    setOpenLocal("");
    setSaving(true); setMsg(null);
    try {
      await api.put("/mensa-matrice/iscrizioni-config", { anno_iscrizioni: null, stato: "aperto", open_at: null });
      setMsg({ ok: true, text: "Configurazione azzerata" });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore reset" });
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 20, color: "#666" }}>Caricamento…</div>;

  const isOpenNow = cfg?.is_open_now !== false;
  const statoLabel = stato === "chiuso" ? "CHIUSO" : (stato === "programmato" ? "PROGRAMMATO" : "APERTO");

  return (
    <div data-testid="extra-impostazioni" style={{ padding: "16px 20px", maxWidth: 900 }}>
      {/* Stato apertura */}
      <div style={{ padding: "14px 18px", background: isOpenNow ? "linear-gradient(90deg,#2e7d32,#1b5e20)" : "linear-gradient(90deg,#c62828,#8e0000)", border: `3px solid ${isOpenNow ? "#1b5e20" : "#8e0000"}`, borderRadius: 6, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", boxShadow: "0 4px 12px rgba(0,0,0,0.25)", marginBottom: 18 }}>
        <div style={{ fontSize: 30 }}>{isOpenNow ? "🟢" : "🔴"}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: "white", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Portale iscrizioni {statoLabel}
          </div>
          <div style={{ fontSize: 11, color: "#fff", lineHeight: 1.5 }}>
            {cfg?.anno_iscrizioni
              ? <>Anno forzato per tutti: <b style={{ background: "white", color: "#1b5e20", padding: "2px 7px", borderRadius: 3 }}>{fmtAnnoScol(cfg.anno_iscrizioni)}</b></>
              : <>Ogni alunno vede i corsi del proprio anno scolastico (automatico).</>
            }
            <br/>
            {cfg?.stato === "programmato" && cfg?.open_at
              ? <>Apertura programmata: <b>{fmtRomaUmana(cfg.open_at)}</b></>
              : cfg?.stato === "aperto"
                ? <>Portale: <b>aperto</b> (gli alunni possono iscriversi subito)</>
                : <>Portale: <b>chiuso</b> (gli alunni vedono &laquo;chiuso&raquo;, nessuna data)</>
            }
            {cfg?.updated_by && <><br/><span style={{ opacity: 0.85 }}>Ultimo cambio: {cfg.updated_by} · {fmtRomaUmana(cfg.updated_at)}</span></>}
          </div>
        </div>
      </div>

      {msg && (
        <div style={{ padding: "8px 12px", background: msg.ok ? "#e8f5e9" : "#ffebee", border: `1px solid ${msg.ok ? "#81c784" : "#ef9a9a"}`, color: msg.ok ? "#1b5e20" : "#c62828", fontSize: 12, marginBottom: 12, borderRadius: 3 }}>
          {msg.text}
        </div>
      )}

      <div style={{ background: "white", border: "1px solid #cfd8dc", borderRadius: 6, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: "#263238", marginBottom: 12, paddingBottom: 8, borderBottom: "2px solid #cfd8dc", textTransform: "uppercase", letterSpacing: 0.5 }}>
          🗓 Stato portale iscrizioni
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {[
            { v: "chiuso", lbl: "🔴 Chiuso (senza data)", desc: "Gli alunni vedono 'Chiuso'." },
            { v: "programmato", lbl: "🟡 Programmato", desc: "Apre alla data sotto. Prima vedono 'Aprirà il…'." },
            { v: "aperto", lbl: "🟢 Aperto subito", desc: "Iscrizioni attive subito." },
          ].map(o => (
            <button key={o.v} type="button" onClick={() => setStato(o.v)}
              data-testid={`impo-stato-${o.v}`}
              style={{
                flex: "1 1 220px", padding: "12px 14px",
                border: `2px solid ${stato === o.v ? "#1565c0" : "#cfd8dc"}`,
                background: stato === o.v ? "#e3f2fd" : "white",
                color: stato === o.v ? "#0d47a1" : "#37474f",
                fontWeight: 700, fontSize: 13, textAlign: "left", cursor: "pointer", borderRadius: 4,
              }}>
              <div>{o.lbl}</div>
              <div style={{ fontSize: 10, fontWeight: 500, marginTop: 4, color: "#666" }}>{o.desc}</div>
            </button>
          ))}
        </div>
        {stato === "programmato" && (
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#37474f", marginBottom: 4, letterSpacing: 0.3 }}>
                DATA E ORA APERTURA (Europe/Rome)
              </div>
              <input
                type="datetime-local"
                data-testid="impo-open-at"
                value={openLocal}
                onChange={e => setOpenLocal(e.target.value)}
                style={{ padding: "8px 10px", border: "2px solid #1565c0", fontSize: 13, fontFamily: "Consolas, monospace" }}
              />
            </div>
            <div style={{ fontSize: 11, color: "#666", flex: "1 1 200px", lineHeight: 1.5 }}>
              Gli alunni vedranno il portale come <b>chiuso</b> con indicata la data di apertura. Si aprirà <b>automaticamente</b> alla data/ora specificata.
            </div>
          </div>
        )}
      </div>

      <div style={{ background: "white", border: "1px solid #cfd8dc", borderRadius: 6, padding: 18, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: "#263238", marginBottom: 12, paddingBottom: 8, borderBottom: "2px solid #cfd8dc", textTransform: "uppercase", letterSpacing: 0.5 }}>
          🎓 Anno scolastico delle iscrizioni
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#37474f", marginBottom: 4, letterSpacing: 0.3 }}>
              ANNO ATTIVO
            </div>
            <select
              data-testid="impo-anno-iscr"
              value={annoIscr}
              onChange={e => setAnnoIscr(e.target.value)}
              style={{ padding: "8px 12px", border: "2px solid #1565c0", fontSize: 13, fontWeight: 700, color: "#0d47a1", minWidth: 220 }}>
              <option value="">⟲ Automatico (anno alunno)</option>
              {anni.map(a => <option key={a} value={a}>Forza anno {fmtAnnoScol(a)}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 11, color: "#666", flex: "1 1 200px", lineHeight: 1.5 }}>
            <b>Automatico</b>: ogni alunno vede i corsi del suo anno scolastico corrente.<br/>
            <b>Forza anno</b>: tutti gli alunni vedranno i corsi di quell'anno (utile in fase di pre-iscrizione AS successivo).<br/>
            <span style={{ color: "#bf360c", fontWeight: 700 }}>⚠ Una scelta errata fa iscrivere gli alunni ai corsi dell'anno sbagliato.</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={reset} disabled={saving} data-testid="impo-reset"
          style={{ padding: "10px 18px", background: "white", color: "#c62828", border: "2px solid #c62828", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 3 }}>
          ↻ Azzera tutto
        </button>
        <button onClick={save} disabled={saving} data-testid="impo-save"
          style={{ padding: "10px 22px", background: "#2e7d32", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: saving ? "wait" : "pointer", borderRadius: 3, letterSpacing: 0.5 }}>
          {saving ? "Salvataggio…" : "💾 Salva configurazione"}
        </button>
      </div>

      <PagamentoConfigSection />
    </div>
  );
}

function PagamentoConfigSection() {
  const [cfg, setCfg] = useState({ intestatario: "", iban: "", banca: "", causale_template: "", istruzioni: "", contatti: "", scadenza_ore: 24 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa-matrice/pagamento-config");
        setCfg({
          intestatario: r.data?.intestatario || "",
          iban: r.data?.iban || "",
          banca: r.data?.banca || "",
          causale_template: r.data?.causale_template || "Iscrizione {corso_descrizione} - {cognome} {nome} - cod. {alunno_cod}",
          istruzioni: r.data?.istruzioni || "",
          contatti: r.data?.contatti || "",
          scadenza_ore: r.data?.scadenza_ore || 24,
        });
      } catch (e) {
        setMsg({ ok: false, text: e.response?.data?.detail || "Errore caricamento" });
      } finally { setLoading(false); }
    })();
  }, []);
  const salva = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.put("/mensa-matrice/pagamento-config", cfg);
      setMsg({ ok: true, text: "Istruzioni pagamento salvate" });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore salvataggio" });
    } finally { setSaving(false); }
  };
  if (loading) return <div style={{ padding: 20, color: "#666" }}>Caricamento istruzioni pagamento…</div>;
  return (
    <div data-testid="pagamento-config-section" style={{ marginTop: 28, background: "white", border: "1px solid #cfd8dc", borderRadius: 6, padding: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 900, color: "#263238", marginBottom: 4, paddingBottom: 8, borderBottom: "2px solid #1565c0", textTransform: "uppercase", letterSpacing: 0.5 }}>
        💳 Istruzioni di pagamento (mail conferma)
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 14, lineHeight: 1.5 }}>
        Questi dati appaiono nella pagina pubblica che il genitore vede cliccando il link nella mail di conferma iscrizione.
      </div>
      {msg && (
        <div style={{ padding: "8px 12px", background: msg.ok ? "#e8f5e9" : "#ffebee", border: `1px solid ${msg.ok ? "#81c784" : "#ef9a9a"}`, color: msg.ok ? "#1b5e20" : "#c62828", fontSize: 12, marginBottom: 12, borderRadius: 3 }}>{msg.text}</div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <PField label="Intestatario conto">
          <input data-testid="pag-intestatario" value={cfg.intestatario} onChange={e => setCfg({ ...cfg, intestatario: e.target.value })}
            placeholder="Istituto Canossiano - Brescia"
            style={pagInpStyle} />
        </PField>
        <PField label="IBAN">
          <input data-testid="pag-iban" value={cfg.iban} onChange={e => setCfg({ ...cfg, iban: e.target.value.toUpperCase() })}
            placeholder="IT00 X000 0000 0000 0000 0000 000"
            style={{ ...pagInpStyle, fontFamily: "Consolas, monospace" }} />
        </PField>
        <PField label="Banca">
          <input data-testid="pag-banca" value={cfg.banca} onChange={e => setCfg({ ...cfg, banca: e.target.value })}
            placeholder="Banca esempio S.p.A." style={pagInpStyle} />
        </PField>
        <PField label="Scadenza conferma (ore)">
          <input data-testid="pag-scadenza" type="number" min="1" max="720" value={cfg.scadenza_ore}
            onChange={e => setCfg({ ...cfg, scadenza_ore: Number(e.target.value) || 24 })}
            style={pagInpStyle} />
        </PField>
      </div>
      <PField label="Template causale (placeholder: {corso_descrizione}, {cognome}, {nome}, {alunno_cod}, {corso_codice}, {anno})">
        <input data-testid="pag-causale-tmpl" value={cfg.causale_template} onChange={e => setCfg({ ...cfg, causale_template: e.target.value })}
          style={{ ...pagInpStyle, width: "100%" }} />
      </PField>
      <PField label="Istruzioni libere (mostrate sopra IBAN nella pagina)">
        <textarea data-testid="pag-istruzioni" value={cfg.istruzioni} onChange={e => setCfg({ ...cfg, istruzioni: e.target.value })}
          rows={4} placeholder="Es: Effettuare bonifico entro 24 ore dalla mail di accettazione. La conferma del pagamento sarà verificata dalla segreteria."
          style={{ ...pagInpStyle, width: "100%", resize: "vertical", fontFamily: "inherit" }} />
      </PField>
      <PField label="Contatti segreteria (telefono, orari, email)">
        <textarea data-testid="pag-contatti" value={cfg.contatti} onChange={e => setCfg({ ...cfg, contatti: e.target.value })}
          rows={2} placeholder="Tel. 030.2886511 · segreteria@canossadiaz.it"
          style={{ ...pagInpStyle, width: "100%", resize: "vertical", fontFamily: "inherit" }} />
      </PField>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={salva} disabled={saving} data-testid="pag-save"
          style={{ padding: "10px 22px", background: "#1565c0", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: saving ? "wait" : "pointer", borderRadius: 3, letterSpacing: 0.5 }}>
          {saving ? "Salvataggio…" : "💾 Salva istruzioni pagamento"}
        </button>
      </div>
    </div>
  );
}

const pagInpStyle = { padding: "8px 10px", border: "1px solid #ccc", fontSize: 12, width: "100%", boxSizing: "border-box" };
function PField({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#37474f", marginBottom: 4, letterSpacing: 0.3 }}>{label}</div>
      {children}
    </div>
  );
}
